import { useStoreAvailableProducts, useStoreOwnStock, useStoreSupplyTimeline } from '@gozoki/api'
import type { StoreAvailableProduct, SupplyHourPoint } from '@gozoki/api-types'
import { frenchFormat } from '@gozoki/tools'
import { cn } from '@gozoki/ui/lib/utils'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import { Bar, ComposedChart, Line, ResponsiveContainer, YAxis } from 'recharts'
import chevronRight from '~/assets/dashboard/chevron-right.svg'
import { ModalCloseButton } from './ui/ModalCloseButton'
import { ProductLotsModal } from './ProductLotsModal'

const PARIS = 'Europe/Paris'
const CHART_HEIGHT = 120
/** Repères horaires posés sur le graphe, à leur vraie position dans la journée. */
const HOUR_MARKS = [6, 12, 18]
const STOCK_COLOR = '#B5CDA6'
const SALES_COLOR = '#F0A945'
const FORECAST_COLOR = '#152013'
const ACCENT_COLOR = '#C58A28'

interface DayBuckets {
  date: string
  isToday: boolean
  label: string
  points: SupplyHourPoint[]
}

/** Regroupe les 168 points horaires de la timeline en une colonne par journée. */
const groupByDay = (hours: SupplyHourPoint[], today: string): DayBuckets[] => {
  const byDate = new Map<string, SupplyHourPoint[]>()
  for (const point of hours) {
    const date = DateTime.fromISO(point.t).setZone(PARIS).toISODate()
    if (!date) continue
    const bucket = byDate.get(date) ?? []
    bucket.push(point)
    byDate.set(date, bucket)
  }

  return [...byDate.entries()].map(([date, points]) => ({
    date,
    isToday: date === today,
    label: `${DateTime.fromISO(date).setLocale('fr').toFormat('ccc').replace('.', '').toUpperCase()}.`,
    points,
  }))
}

function LegendEntry({ children, mark }: { children: string; mark: React.ReactNode }) {
  return (
    <span className="flex items-center gap-[5px] font-sans text-[11px] text-[#072C1A]">
      {mark}
      {children}
    </span>
  )
}

// Mini-graphe d'une journée : stock en barres pâles, ventes en barres orange, prévision en
// pointillés. Deux échelles : le stock se compte en centaines, les ventes en dizaines — une
// échelle unique écraserait les ventes au ras de l'axe.
function DayChart({ day }: { day: DayBuckets }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-[4px] rounded-[10px] bg-[#EFEBDD] px-[6px] pt-[8px] pb-[4px]">
      <span
        className={cn(
          'text-center font-sans text-[10px] font-semibold',
          day.isToday ? 'text-[#C58A28]' : 'text-[#152013]',
        )}
      >
        {day.label}
      </span>

      <div className="relative h-[12px] px-[4px] opacity-55">
        {HOUR_MARKS.map((hour) => (
          <span
            key={hour}
            className="absolute -translate-x-1/2 font-sans text-[8px] text-[#152013]"
            style={{ left: `${(hour / 24) * 100}%` }}
          >
            {String(hour).padStart(2, '0')}:00
          </span>
        ))}
      </div>

      <div style={{ height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={day.points} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
            <YAxis yAxisId="stock" hide domain={[0, 'dataMax']} />
            <YAxis yAxisId="flow" hide domain={[0, 'dataMax']} />
            <Bar yAxisId="stock" dataKey="stock" fill={STOCK_COLOR} isAnimationActive={false} />
            <Bar yAxisId="flow" dataKey="sales" fill={SALES_COLOR} isAnimationActive={false} />
            <Line
              yAxisId="flow"
              type="monotone"
              dataKey="forecastSales"
              stroke={FORECAST_COLOR}
              strokeWidth={1}
              strokeDasharray="3 3"
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function ProductCard({ product, onOpen }: { product: StoreAvailableProduct; onOpen: () => void }) {
  const dlc = product.nearestDlc ? DateTime.fromISO(product.nearestDlc).toFormat('dd/MM') : '—'

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Détail par lot de ${product.label}`}
      className="flex cursor-pointer items-center gap-[14px] rounded-[16px] border border-[#E3E1D8] bg-white p-[15px] text-left"
    >
      {product.imageUrl ? (
        <img
          src={product.imageUrl}
          alt=""
          className="size-[58px] shrink-0 rounded-[12px] object-cover"
        />
      ) : (
        <span className="size-[58px] shrink-0 rounded-[12px] bg-[#EFEBDD]" />
      )}

      <span className="flex min-w-0 flex-1 flex-col gap-[4px]">
        <span className="line-clamp-2 font-sans text-[14px] leading-[18.2px] font-semibold text-[#072C1A]">
          {product.label}
        </span>
        <span className="font-sans text-[13px] text-[#6B6B6B]">
          <span
            className="font-bold"
            style={{ color: product.belowMinimum ? '#DC2626' : '#2A9D5C' }}
          >
            {frenchFormat(product.stock)} unité{product.stock > 1 ? 's' : ''}
          </span>{' '}
          en magasin
        </span>
        <span className="font-sans text-[11px] text-[#6B6B6B]">
          DLC la plus proche : <span className="font-semibold text-[#072C1A]">{dlc}</span>
        </span>
      </span>

      <img src={chevronRight} alt="" aria-hidden className="size-[14px] shrink-0" />
    </button>
  )
}

/**
 * Modale « Produits disponibles » (Figma 10263:1839), ouverte depuis le chevron de la carte
 * FLUX SUPPLY EN TEMPS RÉEL.
 *
 * Rendue DANS le canvas mis à l'échelle, comme les autres modales du dashboard : le `Dialog`
 * partagé passe par un portail sur `document.body`, hors du `transform: scale()`.
 */
export function SupplyDetailModal({ onClose }: { onClose: () => void }) {
  // `undefined` = pastille « Global » : la timeline porte alors la même clé React Query que la
  // carte qui ouvre la modale, donc aucun appel supplémentaire à l'ouverture.
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined)
  const [openedProduct, setOpenedProduct] = useState<StoreAvailableProduct | null>(null)

  const { data: timeline } = useStoreSupplyTimeline(
    categoryId === undefined ? { days: 7 } : { days: 7, scope: 'category', targetId: categoryId },
  )
  const { data: stock } = useStoreOwnStock()
  const { data: availability, isLoading: isProductsLoading } = useStoreAvailableProducts(categoryId)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const now = DateTime.now().setZone(PARIS)
  const todayIso = now.toISODate() ?? ''
  const days = useMemo(
    () => (timeline ? groupByDay(timeline.hours, todayIso) : []),
    [timeline, todayIso],
  )
  const products = availability?.products ?? []

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#072C1A40] p-[32px]"
      role="dialog"
      aria-modal="true"
      aria-label="Produits disponibles"
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-[1308px] max-w-full flex-col gap-[20px] overflow-y-auto kiosk-scroll-y rounded-[20px] bg-[#F0EDE2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-[12px]">
          <h2 className="font-sans text-[22px] leading-[24px] font-semibold whitespace-nowrap text-[#181D27] uppercase">
            Produits disponibles
          </h2>
          <ModalCloseButton onClose={onClose} ariaLabel="Fermer les produits disponibles" />
        </div>

        <p className="font-sans text-[13px] text-[#666]">
          Stock lu en temps réel par les capteurs magasin —{' '}
          <span className="font-semibold">rouge = sous le stock minimum du jour</span>
        </p>

        <div className="flex flex-wrap items-start gap-[10px]">
          <button
            type="button"
            onClick={() => setCategoryId(undefined)}
            aria-pressed={categoryId === undefined}
            className={cn(
              'shrink-0 cursor-pointer rounded-full border px-[19px] py-[11px] font-sans text-[13px] font-semibold',
              categoryId === undefined
                ? 'border-[#507247] bg-[#507247] text-white'
                : 'border-[#D7D7D7] bg-white text-[#072C1A]',
            )}
          >
            Global
          </button>

          {stock?.categories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => setCategoryId(category.id)}
              aria-pressed={categoryId === category.id}
              className={cn(
                'shrink-0 cursor-pointer rounded-full border px-[19px] py-[11px] font-sans text-[13px] font-semibold whitespace-nowrap',
                categoryId === category.id
                  ? 'border-[#507247] bg-[#507247] text-white'
                  : 'border-[#D7D7D7] bg-white text-[#072C1A]',
              )}
            >
              {category.label} · {frenchFormat(category.stock)} u.
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-[10px] rounded-[16px] border border-[#E3E1D8] bg-[#F9F8F1] p-[17px]">
          <div className="flex items-center gap-[10px]">
            <span className="font-sans text-[14px] font-bold text-[#072C1A] uppercase">
              Stocks en temps réel
            </span>
            <span className="font-sans text-[13px] font-bold" style={{ color: ACCENT_COLOR }}>
              - {now.toFormat('HH:mm')}
            </span>

            <span className="flex flex-1 items-center justify-end gap-[16px]">
              <LegendEntry
                mark={
                  <span
                    className="size-[9px] rounded-[3px]"
                    style={{ backgroundColor: STOCK_COLOR }}
                  />
                }
              >
                Stock
              </LegendEntry>
              <LegendEntry
                mark={
                  <span
                    className="size-[9px] rounded-[3px]"
                    style={{ backgroundColor: SALES_COLOR }}
                  />
                }
              >
                Ventes
              </LegendEntry>
              <LegendEntry
                mark={<span className="h-px w-[14px] border-t border-dashed border-[#152013]" />}
              >
                Prévision
              </LegendEntry>
            </span>
          </div>

          {days.length > 0 ? (
            <div className="flex gap-[8px]">
              {days.map((day) => (
                <DayChart key={day.date} day={day} />
              ))}
            </div>
          ) : (
            <div className="flex h-[160px] items-center justify-center font-sans text-[13px] text-[#8A8676]">
              Chargement…
            </div>
          )}
        </div>

        <div className="grid max-h-[420px] grid-cols-4 gap-[14px] overflow-y-auto kiosk-scroll-y">
          {products.map((product) => (
            <ProductCard
              key={product.reference}
              product={product}
              onOpen={() => setOpenedProduct(product)}
            />
          ))}

          {products.length === 0 ? (
            <span className="col-span-4 py-[40px] text-center font-sans text-[14px] text-[#8A8676]">
              {isProductsLoading ? 'Chargement…' : 'Aucun produit en rayon.'}
            </span>
          ) : null}
        </div>
      </div>

      {openedProduct ? (
        <ProductLotsModal product={openedProduct} onClose={() => setOpenedProduct(null)} />
      ) : null}
    </div>
  )
}
